import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import posthog from "posthog-js";
import { useEquipmentSurvey } from "queries/Equipment/useEquipmentSurvey";
import { useBuildingModelHeaderQuery } from "queries/useBuildingModelHeaderQuery";
import classNames from "classnames";
import { CloseButton, WarningIcon } from "components/atoms/Icon";
import { Header } from "components/atoms/Typography";
import SidebarLoading from "components/molecules/SidebarLoading";
import useBuildingLoadingState from "recoilStore/useBuildingLoadingState";
import { getDataCoverageWarnings } from "utils/helpers";
import { Survey } from "../../Survey";
import { DataView } from "../DataView";
import AddEquipmentPrompt from "./AddEquipmentPrompt/AddEquipmentPrompt";
import "./buildingEquipment.scss";
import BuildingEquipmentHeader from "./BuildingEquipmentHeader/BuildingEquipmentHeader";

interface BuildingEquipmentProps {
    agentPrefillEquipmentSurvey?: Partial<EquipmentSurvey> | null;
    onAgentPrefillConsumed?: () => void;
}

function BuildingEquipment({
    agentPrefillEquipmentSurvey: initialAgentPrefillEquipmentSurvey = null,
    onAgentPrefillConsumed,
}: BuildingEquipmentProps) {
    const handleAddEquipmentPrompt = () => {
        setShowSurveyView(true);
    };
    const handleOpenSingleForm = () => {
        setSingleFormModalOpen(true);
        posthog.capture("Single-form equipment modal opened");
    };

    const [showSurveyView, setShowSurveyView] = useState(false);
    const [singleFormModalOpen, setSingleFormModalOpen] = useState(false);
    const [agentPrefillEquipmentSurvey, setAgentPrefillEquipmentSurvey] = useState<Partial<EquipmentSurvey> | null>(
        null
    );
    const { buildingModelUid } = useParams();
    if (!buildingModelUid) throw new Error("buildingModelUid needs to be defined");
    const { data: surveyData, isLoading, isFetching, error } = useEquipmentSurvey(buildingModelUid);

    const { loadingState } = useBuildingLoadingState(buildingModelUid);

    const { isLoading: buildingStatusLoading, data: buildingHeaderData } =
        useBuildingModelHeaderQuery(buildingModelUid);

    const surveyDataExists = useMemo(() => {
        if (!surveyData) return false;
        return true;
    }, [surveyData]);

    const dataCoverageWarnings = useMemo(
        () => (buildingHeaderData?.dataCoverage ? getDataCoverageWarnings(buildingHeaderData.dataCoverage) : undefined),
        [buildingHeaderData]
    );

    const showEquipmentWarnings = useMemo(() => dataCoverageWarnings?.equipmentWarning, [dataCoverageWarnings]);

    const hasNaturalGasWarning = useMemo(() => dataCoverageWarnings?.hasNaturalGasWarning, [dataCoverageWarnings]);

    useEffect(() => {
        if (!initialAgentPrefillEquipmentSurvey) return;
        setAgentPrefillEquipmentSurvey(initialAgentPrefillEquipmentSurvey);
        setSingleFormModalOpen(true);
        posthog.capture("Single-form equipment modal opened from topology extraction");
        onAgentPrefillConsumed?.();
    }, [initialAgentPrefillEquipmentSurvey, onAgentPrefillConsumed]);

    useEffect(() => {
        if (!singleFormModalOpen) {
            setAgentPrefillEquipmentSurvey(null);
        }
    }, [singleFormModalOpen]);

    if (isLoading || loadingState.equipmentSidebarLoading || buildingStatusLoading || isFetching)
        return <SidebarLoading />;
    if (error) return <>Error</>;

    if (surveyDataExists && surveyData && !showSurveyView)
        return (
            <div className="building-equipment-sidebar">
                {singleFormModalOpen && (
                    <Survey
                        setShowSurveyView={setShowSurveyView}
                        singleFormModalOpen={singleFormModalOpen}
                        setSingleFormModalOpen={setSingleFormModalOpen}
                        showSurveyView={showSurveyView}
                        persistedEquipmentSurvey={surveyData ?? null}
                        agentPrefillEquipmentSurvey={agentPrefillEquipmentSurvey}
                    />
                )}
                <BuildingEquipmentHeader
                    editSurveyWizard={() => {
                        setShowSurveyView(true);
                    }}
                    editSurveySingleForm={handleOpenSingleForm}
                    buildingName={buildingHeaderData?.buildingName ?? ""}
                />
                {showEquipmentWarnings && !hasNaturalGasWarning && <MissingNaturalGasHeader />}
                <DataView
                    data={surveyData}
                    showEquipmentWarning={showEquipmentWarnings}
                    hasNaturalGasWarning={hasNaturalGasWarning}
                />
            </div>
        );

    return (
        <div
            className={classNames(
                "building-equipment-sidebar",
                showSurveyView && "building-equipment-sidebar--survey-view"
            )}
        >
            {showSurveyView || singleFormModalOpen ? (
                <>
                    <div className="building-equipment-sidebar--survey-view__header">
                        <Header size="medium">Add equipment</Header>
                        <CloseButton
                            onClick={() => {
                                setShowSurveyView(false);
                                posthog.capture("Equipment closed");
                            }}
                        />
                    </div>
                    <Survey
                        setShowSurveyView={setShowSurveyView}
                        singleFormModalOpen={singleFormModalOpen}
                        setSingleFormModalOpen={setSingleFormModalOpen}
                        showSurveyView={showSurveyView}
                        persistedEquipmentSurvey={surveyData ?? null}
                        agentPrefillEquipmentSurvey={agentPrefillEquipmentSurvey}
                    />
                </>
            ) : (
                <AddEquipmentPrompt onClickAdd={handleAddEquipmentPrompt} onClickSingleForm={handleOpenSingleForm} />
            )}
        </div>
    );
}

function MissingNaturalGasHeader() {
    return (
        <div className="missing-natural-gas-header">
            <Header size="small" style={{ color: "#CA8727", display: "flex", gap: "8px" }}>
                Missing Natural Gas <WarningIcon />
            </Header>
        </div>
    );
}

export default BuildingEquipment;
